import { convertDateFormat, getLastSunday, getTimeToday } from '@/utils/format.js'
import {
  confirmAppointment,
  confirmAppointmentToUser,
  deleteAppointment,
  exportAppointment,
  extendAppointmentLock,
  getAppointmentTimeslots,
  getFutureAppointments,
  getMonthlyAppointmentAvailability,
  getPastAppointments,
  lockAppointmentTimeslot,
  notifyDeletedAppointmentEmail,
  notifyDeletedAppointmentSms,
  updateAppointmentContact,
} from '../api/myTermine'
import { getCalender, getGroupedAppointments } from '../api/otbBeratung'

// eslint-disable-next-line
import { Appointment } from '../types/appointment'
// eslint-disable-next-line
import { OtbAppointment } from '../types/otbBeratung'

function defaultAppointmentState() {
  return {
    dayTime: {
      Montag: false,
      Dienstag: false,
      Mittwoch: false,
      Donnerstag: false,
      Freitag: false,
      Samstag: false,
      NurVormittags: false,
      NurNachmittags: false,
    },
    singleDate: convertDateFormat(new Date()), // today
    firstDateOfWeek: getLastSunday(),
    dayFiltersStartDates: [],
    dayFiltersStartDatesIndex: 0,
    /** @type {Appointment[]} */
    futureAppointments: [],
    /** @type {Appointment[]} */
    pastAppointments: [],
    previousAppointmentId: null,
    /**
     * The currently confirmed appointments (can be multiple in certain cases)
     * @type {Appointment[]} */
    currentAppointments: [],
    isTimeslotsLoading: false,
    timeslots: [],
    lastTimeslot: null,
    datesWithTimeslots: [],
    selectedTimeslot: null,
    lockedTimeslot: null,
    /** @type {OtbAppointment[]} */
    futureOtbAppointments: [],
    /** @type {OtbAppointment[]} */
    pastOtbAppointments: [],
  }
}

export default {
  state: () => ({
    ...defaultAppointmentState(),
  }),
  actions: {
    async getUserAppointments({ dispatch }) {
      dispatch('getFutureAppointments')
      dispatch('getPastAppointments')
    },
    async getFutureAppointments({ commit, rootState }) {
      try {
        const response = await getFutureAppointments(rootState.user.user.pnr)
        commit('setFutureAppointments', response.data.items)
      } catch (error) {
        console.error(error)
      }
    },
    async getPastAppointments({ commit, rootState }) {
      try {
        const response = await getPastAppointments(rootState.user.user.pnr)
        commit('setPastAppointments', response.data.items?.slice(-3))
      } catch (error) {
        console.error(error)
      }
    },
    async exportAppointment(context, id) {
      try {
        const response = await exportAppointment(id)
        let fileUrl = window.URL.createObjectURL(response.data)
        let fileLink = document.createElement('a')

        fileLink.href = fileUrl
        fileLink.setAttribute('download', id + '.ics')
        document.body.appendChild(fileLink)
        fileLink.click()
      } catch (error) {
        console.error(error)
      }
    },
    async exportOtbAppointment(context, id) {
      try {
        const response = await getCalender(id)
        let fileUrl = window.URL.createObjectURL(response.data)
        let fileLink = document.createElement('a')

        fileLink.href = fileUrl
        fileLink.setAttribute('download', id + '.ics')
        document.body.appendChild(fileLink)
        fileLink.click()
      } catch (error) {
        console.error(error)
      }
    },
    async deleteAppointment({ dispatch }, id) {
      await dispatch('notifyDelete', id)
      try {
        await deleteAppointment(id)
      } catch (error) {
        console.error(error)
      }
    },
    async deleteLockedAppointment({ commit, state }) {
      try {
        await deleteAppointment(state.lockedTimeslot[0].id)
        commit('clearLockedTimeslot')
      } catch (error) {
        console.error(error)
      }
    },
    async getAppointmentTimeslots({ commit, rootState }, { date, startTime, days }) {
      const timeToday = date === convertDateFormat(new Date()) && getTimeToday()

      const payload = {
        datum: date,
        startzeitpunkt: startTime || timeToday || 0,
        dienststelleId: rootState.station.activeStationId,
        snrs: rootState.service.selectedServicesId,
        kfzartId:
          rootState.vehicle.selectedVehicle?.kfzartId || rootState.vehicle.newVehicle?.kfzartId,
        days,
      }

      try {
        commit('setIsTimeslotsLoading', true)
        const response = await getAppointmentTimeslots(payload)
        commit('addTimeslots', response.data.items)
        commit('setLastTimeslot', response.data.items[response.data.items.length - 1])
        if (!response.data.items.length) commit('setIsTimeslotsLoading', false)
      } catch (error) {
        console.error(error)
      }
    },

    /**
     * Get monthly availability for appointment booking using the new monthly endpoint.
     * This significantly reduces the number of API calls from ~30 individual calls per month
     * to just 1 call per month, improving performance.
     *
     * @param {Object} context - Vuex action context
     * @param {Object} payload - Action payload
     * @param {string} payload.date - The date in YYYY-MM-DD format for the month to fetch
     */
    async getMonthlyAvailability({ commit, rootState }, { date }) {
      const payload = {
        datum: date,
        dienststelleId: rootState.station.activeStationId,
        snrs: rootState.service.selectedServicesId,
        kfzartId:
          rootState.vehicle.selectedVehicle?.kfzartId || rootState.vehicle.newVehicle?.kfzartId,
      }
      try {
        const response = await getMonthlyAppointmentAvailability(payload)
        commit('setMonthlyDatesWithTimeslots', response.data.items)
        return response
      } catch (error) {
        console.error('Error fetching monthly availability:', error)
        throw error
      }
    },
    /**
     * Fetches a specific timeslot from the API based on date and time.
     * Validates that all required data (station, services, vehicle) is available before making the API call.
     * 
     * @param {Object} context - Vuex action context
     * @param {Object} params - Parameters for timeslot search
     * @param {string} params.date - Date in YYYY-MM-DD format
     * @param {number} params.startTime - Start time in minutes from midnight
     */
    async getSelectedTimeslot({ commit, rootState }, { date, startTime }) {
      // Validate required data before API call to prevent errors
      if (!rootState.station.activeStationId) {
        console.error('Cannot get timeslot: activeStationId is missing')
        commit('setSelectedTimeslot', false)
        return
      }

      if (!rootState.service.selectedServicesId?.length) {
        console.error('Cannot get timeslot: selectedServicesId is missing')
        commit('setSelectedTimeslot', false)
        return
      }

      const kfzartId =
        rootState.vehicle.selectedVehicle?.kfzartId || rootState.vehicle.newVehicle?.kfzartId
      if (!kfzartId) {
        console.error('Cannot get timeslot: kfzartId is missing')
        commit('setSelectedTimeslot', false)
        return
      }

      const payload = {
        datum: date,
        startzeitpunkt: startTime - 1,
        dienststelleId: rootState.station.activeStationId,
        snrs: rootState.service.selectedServicesId,
        kfzartId: kfzartId,
      }

      const params = {
        maxresults: 2,
      }

      try {
        const response = await getAppointmentTimeslots(payload, params)
        
        // Find the requested timeslot in the API response
        const selectedTimeslot = response.data.items.find(
          (item) => item.datum === date && item.von === startTime
        )

        if (selectedTimeslot) {
          // Handle stacked services (multiple services in one appointment)
          if (selectedTimeslot.stackId) {
            const timeslots = response.data.items.filter(
              (element) => element.stackId === selectedTimeslot.stackId
            )
            commit('setSelectedTimeslot', timeslots)
          } else {
            commit('setSelectedTimeslot', [selectedTimeslot])
          }
        } else {
          // Timeslot not found - may have been booked by someone else or expired
          commit('setSelectedTimeslot', false)
        }
      } catch (error) {
        commit('setSelectedTimeslot', false)
        console.error(error)
      }
    },
		/**
		 * Locks a timeslot for the user to prevent others from booking it.
		 * This gives the user 10 minutes to complete the booking.
		 * Validates all required data before making the API call to prevent incomplete bookings.
		 * 
		 * @throws {Error} If any required data (user, vehicle, station, timeslot) is missing
		 */
		async lockAppointmentTimeslot({ commit, rootState, state }) {
			// Validate user data to prevent booking with incomplete information
			const isCompany = rootState.user?.user?.isCompany
			if (!rootState.user?.user?.pnr || (!isCompany && !rootState.user?.user?.firstname) || !rootState.user?.user?.lastname || !rootState.user?.user?.email) {
				console.error('Cannot lock appointment: User data is incomplete', {
					pnr: !!rootState.user?.user?.pnr,
					firstname: !!rootState.user?.user?.firstname,
					lastname: !!rootState.user?.user?.lastname,
					email: !!rootState.user?.user?.email,
				})
				throw new Error('User information is not available. Please refresh and try again.')
			}

			// Validate vehicle data
			if (!rootState.vehicle.selectedVehicle && !rootState.vehicle.newVehicle) {
				console.error('Cannot lock appointment: No vehicle selected')
				throw new Error('Vehicle information is required for booking.')
			}

			// Validate station data
			if (!rootState.station.activeStationId) {
				console.error('Cannot lock appointment: No station selected')
				throw new Error('Station information is required for booking.')
			}

			// Validate selectedTimeslot exists and has data
			if (!state.selectedTimeslot || state.selectedTimeslot === false || !Array.isArray(state.selectedTimeslot) || !state.selectedTimeslot[0]) {
				console.error('Cannot lock appointment: No timeslot selected', {
					selectedTimeslot: state.selectedTimeslot
				})
				throw new Error('No appointment timeslot selected. Please select a timeslot.')
			}

      // Validate vehicle data
      if (!rootState.vehicle.selectedVehicle && !rootState.vehicle.newVehicle) {
        console.error('Cannot lock appointment: No vehicle selected')
        throw new Error('Vehicle information is required for booking.')
      }

      // Validate station data
      if (!rootState.station.activeStationId) {
        console.error('Cannot lock appointment: No station selected')
        throw new Error('Station information is required for booking.')
      }

      // Validate selectedTimeslot
      if (
        !state.selectedTimeslot ||
        state.selectedTimeslot === false ||
        !Array.isArray(state.selectedTimeslot) ||
        !state.selectedTimeslot[0]
      ) {
        console.error('Cannot lock appointment: No timeslot selected', {
          selectedTimeslot: state.selectedTimeslot,
        })
        throw new Error('No appointment timeslot selected. Please select a timeslot.')
      }

      let terminFahrzeugModel

      if (rootState.vehicle.selectedVehicle) {
        const selectedVehicle = rootState.vehicle.selectedVehicle
        terminFahrzeugModel = {
          allrad: selectedVehicle.fourWheelDrive || false, // in case of null
          diesel: selectedVehicle.antriebsart === 'Diesel',
          gebremst: selectedVehicle.gebremst,
          marke: selectedVehicle.brand,
          typ: selectedVehicle.model,
          kennzeichen: selectedVehicle.licensePlate,
          fin: selectedVehicle.fin,
        }
      } else if (rootState.vehicle.newVehicle) {
        const newVehicle = rootState.vehicle.newVehicle

        terminFahrzeugModel = {
          allrad: newVehicle.fourWheelDrive,
          diesel: newVehicle.engine === 'Diesel',
          gebremst: newVehicle.gebremst,
          marke: newVehicle.brand,
          typ: newVehicle.model,
          kennzeichen: newVehicle.licensePlate,
          fin: newVehicle.fin,
        }
      }

      const payload = {
        terminsucheModel: {
          datum: state.selectedTimeslot[0].datum,
          startzeitpunkt: state.selectedTimeslot[0].von,
          kfzartId: state.selectedTimeslot[0].kfzArtId,
          dienststelleId: rootState.station.activeStationId,
          snrs: rootState.service.selectedServicesId,
        },
        kundeModel: {
          clubkartenId: rootState.user.user.pnr,
          vorname: rootState.user.user.firstname || '',
          nachname: rootState.user.user.lastname,
          plz: rootState.user.userAccountInfo?.address.zip,
          email: rootState.user.user.email,
          telefonMobil: rootState.user.userAccountInfo?.phone,
        },
        personVertragId: 0,
        terminFahrzeugModel,
      }

      try {
        const response = await lockAppointmentTimeslot(state.selectedTimeslot[0].id, payload)
        commit('setLockedTimeslot', response.data.items)
      } catch (error) {
        console.error(error)
      }
    },
    async extendAppointmentLock({ state }) {
      if (!state.lockedTimeslot) return
      const appointmentId = state.lockedTimeslot[0].id
      try {
        await extendAppointmentLock(appointmentId)
      } catch (error) {
        console.error(error)
      }
    },
    async updateAppointmentContact({ state }, { phone, email }) {
      const payload = {
        terminIds: state.lockedTimeslot.map((appointment) => appointment.id),
        telefonMobil: phone,
        email: email,
      }
      try {
        await updateAppointmentContact(payload)
      } catch (error) {
        console.error(error)
      }
    },
    async confirmAppointment({ commit, state }) {
      const payload = {
        terminIds: state.lockedTimeslot.map((appointment) => appointment.id),
        isConfirm: true,
      }
      try {
        const response = await confirmAppointment(payload)
        console.log('confirmAppointment response', response)
        commit('setCurrentAppointments', response.data.items)
      } catch (error) {
        console.error(error)
      }
    },
    async confirmAppointmentToUser({ state }, { phone, email }) {
      const payload = {
        terminIds: state.lockedTimeslot.map((appointment) => appointment.id),
        telefonMobil: phone,
        email: email,
      }
      try {
        await confirmAppointmentToUser(payload)
      } catch (error) {
        console.error(error)
      }
    },
    async getOtbAppointments({ commit, rootState }) {
      try {
        const response = await getGroupedAppointments(rootState.user.user.pnr)
        commit('setFutureOtbAppointments', response.data.upcomingAppointments)
        commit('setPastOtbAppointments', response.data.pastAppointments)
      } catch (error) {
        console.error(error)
      }
    },
    async notifyDelete(_, appointmentId) {
      const payload = {
        terminId: appointmentId,
      }
      try {
        await notifyDeletedAppointmentSms(payload)
      } catch (error) {
        console.error(error)
      }
      try {
        await notifyDeletedAppointmentEmail(payload)
      } catch (error) {
        console.error(error)
      }
    },
  },
  mutations: {
    resetAppointmentState(state) {
      // TODO: keep info on current appointments if they exist
      const savedAppointments = state.currentAppointments
      Object.assign(state, defaultAppointmentState())
      if (savedAppointments && savedAppointments.length > 0) {
        state.currentAppointments = savedAppointments
      }
    },
    setDayTime(state, payload) {
      state.dayTime = payload
    },
    setSingleDate(state, payload) {
      state.singleDate = payload
    },
    setFirstDateOfWeek(state, payload) {
      state.firstDateOfWeek = payload
    },
    setDayFiltersStartDates(state, payload) {
      state.dayFiltersStartDates = payload
    },
    setDayFiltersStartDatesIndex(state, payload) {
      state.dayFiltersStartDatesIndex = payload
    },
    setFutureAppointments(state, payload) {
      state.futureAppointments = payload
    },
    setPastAppointments(state, payload) {
      state.pastAppointments = payload
    },
    setPreviousAppointmentId(state, payload) {
      state.previousAppointmentId = payload
    },
    setIsTimeslotsLoading(state, payload) {
      state.isTimeslotsLoading = payload
    },
    addTimeslots(state, payload) {
      state.timeslots.push(...payload)
    },
    clearTimeslots(state) {
      state.timeslots = []
    },
    setLastTimeslot(state, payload) {
      state.lastTimeslot = payload
    },
    clearLastTimeslot(state) {
      state.lastTimeslot = null
    },
    setMonthlyDatesWithTimeslots(state, monthlyItems) {
      // monthlyItems is an object like { "2025-08-06": false, "2025-08-07": true, ... }
      // We need to extract dates where value is true and add them to datesWithTimeslots
      Object.entries(monthlyItems).forEach(([date, hasAvailability]) => {
        if (hasAvailability && !state.datesWithTimeslots.includes(date)) {
          state.datesWithTimeslots.push(date)
        }
      })
    },
    clearDatesWithTimeslots(state) {
      state.datesWithTimeslots = []
    },
    setSelectedTimeslot(state, payload) {
      state.selectedTimeslot = payload
    },
    clearSelectedTimeslot(state) {
      state.selectedTimeslot = null
    },
    setLockedTimeslot(state, payload) {
      state.lockedTimeslot = payload
      // Persist to sessionStorage for page reload recovery
      if (payload) {
        try {
          sessionStorage.setItem('tvg_lockedTimeslot', JSON.stringify(payload))
        } catch (error) {
          console.error('Failed to save lockedTimeslot to sessionStorage:', error)
        }
      }
    },
    clearLockedTimeslot(state) {
      state.lockedTimeslot = null
      // Also clear from sessionStorage
      try {
        sessionStorage.removeItem('tvg_lockedTimeslot')
      } catch (error) {
        console.error('Failed to remove lockedTimeslot from sessionStorage:', error)
      }
    },
    setFutureOtbAppointments(state, payload) {
      state.futureOtbAppointments = payload
    },
    setPastOtbAppointments(state, payload) {
      state.pastOtbAppointments = payload
    },
    setCurrentAppointments(state, payload) {
      state.currentAppointments = payload
    },
  },
  getters: {
    dayTime(state) {
      return state.dayTime
    },
    singleDate(state) {
      return state.singleDate
    },
    firstDateOfWeek(state) {
      return state.firstDateOfWeek
    },
    dayFiltersStartDates(state) {
      return state.dayFiltersStartDates
    },
    dayFiltersStartDatesIndex(state) {
      return state.dayFiltersStartDatesIndex
    },
    futureAppointments(state) {
      return state.futureAppointments
    },
    pastAppointments(state) {
      return state.pastAppointments
    },
    previousAppointmentId(state) {
      return state.previousAppointmentId
    },
    isTimeslotsLoading(state) {
      return state.isTimeslotsLoading
    },
    timeslots(state) {
      return state.timeslots
    },
    lastTimeslot(state) {
      return state.lastTimeslot
    },
    datesWithTimeslots(state) {
      return state.datesWithTimeslots
    },
    selectedTimeslot(state) {
      return state.selectedTimeslot
    },
    lockedTimeslot(state) {
      return state.lockedTimeslot
    },
    /** @returns {Array<Appointment|OtbAppointment>} Mixed Array of Appointments sorted by Date, oldest date comes first */
    combinedFutureAppointments(state) {
      return [...state.futureAppointments, ...state.futureOtbAppointments].sort((a, b) => {
        const aDate = new Date(a.startsAt || a.datum)
        const bDate = new Date(b.startsAt || b.datum)
        return aDate - bDate
      })
    },
    /** @returns {Array<Appointment|OtbAppointment>} Mixed Array of Appointments sorted by Date, newest date comes first */
    combinedPastAppointments(state) {
      return [...state.pastAppointments, ...state.pastOtbAppointments].sort((a, b) => {
        const aDate = new Date(a.startsAt || a.datum)
        const bDate = new Date(b.startsAt || b.datum)
        return bDate - aDate
      })
    },
    currentAppointments(state) {
      return state.currentAppointments
    },
    // Backward compatibility getter for single appointment (returns first appointment)
    currentAppointment(state) {
      return state.currentAppointments && state.currentAppointments.length > 0
        ? state.currentAppointments[0]
        : null
    },
  },
}
